import { useEffect, useState } from 'react'
import {
    Area,
    AreaChart,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { boardQuery } from '~/api/monitoring'
import type { BoardCard, BoardQueryResult } from '~/api/types'
import DOMPurify from 'dompurify'
import { Markdown } from '~/components/ui/markdown'
import { CHART_UI, VERDICT_COLOR } from '~/features/monitoring/chart-colors'

/** 카드 1개 — 보드 쿼리 데이터를 카드 타입에 맞게 렌더(시계열/통계/파이/표). */
export function BoardCardBody({
    dashboardId,
    card,
}: {
    dashboardId: string
    card: BoardCard
}) {
    const [data, setData] = useState<BoardQueryResult | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (card.type === 'text' || !card.fieldId) {
            setData(null)
            setLoading(false)
            return
        }
        setLoading(true)
        boardQuery(dashboardId, card.fieldId, {
            dateFrom: card.dateFrom,
            dateTo: card.dateTo,
            windowKind: card.windowKind,
            windowN: card.windowN,
        })
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false))
    }, [
        dashboardId,
        card.type,
        card.fieldId,
        card.dateFrom,
        card.dateTo,
        card.windowKind,
        card.windowN,
    ])

    if (card.type === 'text') {
        return <TextBody text={card.text ?? ''} format={card.format} />
    }
    if (!card.fieldId) {
        return (
            <p className='text-muted-foreground flex h-full items-center justify-center text-center text-xs'>
                지표를 선택하세요
            </p>
        )
    }
    if (loading) {
        return (
            <p className='text-muted-foreground flex h-full items-center justify-center text-xs'>
                불러오는 중…
            </p>
        )
    }
    if (!data) {
        return (
            <p className='text-muted-foreground flex h-full items-center justify-center text-xs'>
                데이터 없음
            </p>
        )
    }

    if (card.type === 'stat') return <StatBody data={data} card={card} />
    if (card.type === 'pie') return <PieBody data={data} />
    if (card.type === 'table') return <TableBody data={data} />
    return <TimeseriesBody data={data} />
}

const METRIC_LABEL: Record<string, string> = {
    avg: '평균',
    median: '중앙값',
    last: '최근값',
    max: '최대값',
    min: '최소값',
}

/**
 * HTML 새니타이즈 — DOMPurify(업계 표준)로 스크립트·이벤트핸들러·javascript: URL 등을 제거한다.
 * 정규식 새니타이저는 우회 가능(XSS)해 폐기했다. 보드 텍스트카드는 AI 빌더/사용자가 작성한다.
 */
function sanitizeHtml(html: string): string {
    return DOMPurify.sanitize(html)
}

function TextBody({
    text,
    format,
}: {
    text: string
    format?: BoardCard['format']
}) {
    if (!text.trim()) {
        return (
            <p className='text-muted-foreground flex h-full items-center justify-center text-center text-xs'>
                편집 모드에서 텍스트를 입력하세요
            </p>
        )
    }
    return (
        <div className='h-full overflow-auto'>
            {format === 'txt' ? (
                <div className='text-sm whitespace-pre-wrap'>{text}</div>
            ) : format === 'html' ? (
                <div
                    className='text-sm [&_*]:break-words'
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }}
                />
            ) : (
                <Markdown>{text}</Markdown>
            )}
        </div>
    )
}

function TimeseriesBody({ data }: { data: BoardQueryResult }) {
    const points = data.series
        .map((s) => ({
            t: (s.measuredAt ?? '').slice(5, 16).replace('T', ' '),
            v: Number((s.value ?? '').replace(/,/g, '')),
        }))
        .filter((p) => Number.isFinite(p.v))
    if (points.length === 0) {
        return <Empty />
    }
    return (
        <ResponsiveContainer width='100%' height='100%'>
            <AreaChart
                data={points}
                margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <defs>
                    <linearGradient id='bc' x1='0' y1='0' x2='0' y2='1'>
                        <stop
                            offset='0%'
                            stopColor={CHART_UI.line}
                            stopOpacity={0.2}
                        />
                        <stop
                            offset='100%'
                            stopColor={CHART_UI.line}
                            stopOpacity={0}
                        />
                    </linearGradient>
                </defs>
                <XAxis
                    dataKey='t'
                    tick={{ fontSize: 10, fill: CHART_UI.axis }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={28}
                />
                <YAxis
                    tick={{ fontSize: 10, fill: CHART_UI.axis }}
                    tickLine={false}
                    axisLine={false}
                    width={32}
                />
                <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    labelFormatter={(l) => `측정: ${l}`}
                />
                <Area
                    type='monotone'
                    dataKey='v'
                    stroke={CHART_UI.line}
                    strokeWidth={2}
                    fill='url(#bc)'
                />
            </AreaChart>
        </ResponsiveContainer>
    )
}

function StatBody({ data, card }: { data: BoardQueryResult; card: BoardCard }) {
    const s = data.stat
    // 측정값이 0건이면 통계 대시(—) 대신 행동 가능한 안내를 보여준다.
    if (!s.count) return <Empty />
    const metric = card.statMetric ?? 'avg'
    const value: number | string | null =
        metric === 'last'
            ? s.last
            : metric === 'avg'
              ? s.avg
              : metric === 'median'
                ? s.median
                : metric === 'max'
                  ? s.max
                  : s.min
    const windowLabel =
        card.windowKind === 'count'
            ? `최근 ${card.windowN ?? 7}개`
            : card.windowKind === 'days'
              ? `최근 ${card.windowN ?? 7}일`
              : '전체 기간'
    return (
        <div className='flex h-full flex-col justify-center'>
            <p className='text-muted-foreground text-[11px]'>
                {METRIC_LABEL[metric]} · {windowLabel}
            </p>
            <p className='text-foreground text-3xl font-bold'>
                {value != null ? value : '—'}
                {data.unit ? (
                    <span className='text-muted-foreground ml-1 text-sm font-normal'>
                        {data.unit}
                    </span>
                ) : null}
            </p>
            <div className='text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]'>
                <span>평균 {s.avg ?? '—'}</span>
                <span>중앙값 {s.median ?? '—'}</span>
                <span>최소 {s.min ?? '—'}</span>
                <span>최대 {s.max ?? '—'}</span>
                <span>측정 {s.count}건</span>
            </div>
        </div>
    )
}

function PieBody({ data }: { data: BoardQueryResult }) {
    const slices = data.distribution.filter((d) => d.count > 0)
    if (slices.length === 0) return <Empty />
    return (
        <ResponsiveContainer width='100%' height='100%'>
            <PieChart>
                <Pie
                    data={slices}
                    dataKey='count'
                    nameKey='verdict'
                    innerRadius='45%'
                    outerRadius='75%'
                    paddingAngle={2}>
                    {slices.map((s) => (
                        <Cell
                            key={s.verdict}
                            fill={VERDICT_COLOR[s.verdict] ?? CHART_UI.neutral}
                        />
                    ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            </PieChart>
        </ResponsiveContainer>
    )
}

function TableBody({ data }: { data: BoardQueryResult }) {
    const rows = [...data.series].reverse().slice(0, 60)
    if (rows.length === 0) return <Empty />
    return (
        <div className='h-full overflow-auto'>
            <table className='w-full text-xs'>
                <thead className='bg-card sticky top-0'>
                    <tr className='text-muted-foreground border-border border-b'>
                        <th className='py-1.5 pr-2 text-left'>측정 시각</th>
                        <th className='py-1.5 pr-2 text-left'>값</th>
                        <th className='py-1.5 text-left'>판정</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr
                            key={i}
                            className='border-border border-b last:border-0'>
                            <td className='text-muted-foreground py-1.5 pr-2'>
                                {(r.measuredAt ?? '').slice(0, 16)}
                            </td>
                            <td className='text-foreground py-1.5 pr-2'>
                                {r.value}
                            </td>
                            <td
                                className='py-1.5'
                                style={{
                                    color:
                                        VERDICT_COLOR[r.verdict] ??
                                        CHART_UI.axis,
                                }}>
                                {r.verdict}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

function Empty() {
    return (
        <div className='text-muted-foreground flex h-full flex-col items-center justify-center gap-1 px-3 text-center text-xs'>
            <span>이 지표에 적재된 측정값이 없어요.</span>
            <span className='text-[11px]'>
                「데이터 적재」로 올리거나, 카드 설정(톱니)에서 데이터가 있는
                지표로 바꿔 보세요.
            </span>
        </div>
    )
}
